import { can, canAny } from "@/app/auth/can";
import { UserType, type UserEntity } from "@/shared/types/user.entity";

/**
 * Which permission each clients affordance needs — one named function per button, so a reviewer can read the
 * requirement without opening the component.
 *
 * Every value was taken from the `@JwtAuth` decorator of the endpoint the affordance calls. They are DEFAULT
 * DENY. They used to end in `return true` for every role except `operator`, which meant an invited member —
 * whose 25 permissions are all `:read` — saw create, edit and delete buttons that the API answered with 401.
 * The role branches are gone entirely: `role` decides nothing in the backend, and an internal admin reaches
 * these through `full_admin_access` like everyone else.
 */

export function canReadClients(user: UserEntity | null | undefined): boolean {
  // GET /internal/clients/:id — clients.internal.controller.ts:238
  return can(user, "clients:read");
}

export function canWriteClients(user: UserEntity | null | undefined): boolean {
  // POST /internal/clients — clients.internal.controller.ts:55
  return can(user, "clients:write");
}

export function canEditSubclient(user: UserEntity | null | undefined): boolean {
  // PATCH /internal/clients/:id — clients.internal.controller.ts:265
  return can(user, "clients:write");
}

export function canReadClientAccounts(user: UserEntity | null | undefined): boolean {
  // The modal lists both a subclient's accounts and an organization's, from two controllers.
  return canAny(user, ["client_accounts:read", "organization_accounts:read"]);
}

export function canWriteClientAccounts(user: UserEntity | null | undefined): boolean {
  return canAny(user, ["client_accounts:write", "organization_accounts:write"]);
}

/**
 * Editing an organization is INTERNAL-only by PRODUCT decision, not by tenancy: the backend's `PATCH
 * /internal/organizations/:id` asks for `organizations:update` and declares no `userTypes`, so an
 * organization admin holding the write role could do it through the API today. Opening that up is a product
 * change that needs its own decision — widening access is not a side effect a refactor should have. Delete
 * the user-type line when that decision is taken.
 *
 * `organizations:write`, not `:update`: the projection in `scopes[]` carries atoms only, and the guard
 * collapses `update` to `write` before evaluating.
 */
export function canEditOrganization(user: UserEntity | null | undefined): boolean {
  if (user?.user_type !== UserType.INTERNAL) return false;
  return can(user, "organizations:write");
}

/**
 * Deleting a client. No user-type branch, unlike {@link canEditOrganization}: the endpoint declares no
 * `userTypes`, and an organization admin holds `clients:delete` and manages its own clients. Tenancy is the
 * backend's `assertOrgAccess`, which 404s a cross-org target.
 *
 * The backend refuses (409) while the client still owns payment accounts. That is a state check, not a
 * permission one, so it lives in the view — see `ViewClientModal`.
 */
export function canDeleteClients(user: UserEntity | null | undefined): boolean {
  // DELETE /internal/clients/:id — clients.internal.controller.ts:309
  return can(user, "clients:delete");
}

export function canDeleteOrganizations(user: UserEntity | null | undefined): boolean {
  // DELETE /internal/organizations/:id — organizations.internal.controller.ts:225
  return can(user, "organizations:delete");
}

export function canDeleteSubclientAccounts(user: UserEntity | null | undefined): boolean {
  // DELETE /internal/clients/:clientId/accounts/:id — client-accounts.internal.controller.ts:179
  return can(user, "client_accounts:delete");
}

export function canDeleteOrganizationAccounts(user: UserEntity | null | undefined): boolean {
  // DELETE .../organization-accounts/:id — organization-account-create.internal.controller.ts:192
  return can(user, "organization_accounts:delete");
}
